import { useState, useEffect } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { cn } from '../../../utils/cn';
import { useMoney } from '../../../providers/CurrencyProvider';
import { useI18n } from '../../../i18n';

/** Theo dõi breakpoint mobile (< 640px) để chỉnh các thông số biểu đồ vốn là
 *  props JS của recharts (không set qua CSS được): độ rộng trục, cỡ chữ, mật độ nhãn. */
function useIsMobile(query = '(max-width: 639px)'): boolean {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false
  );
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setIsMobile(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);
  return isMobile;
}

export interface RevenueDataPoint {
  /** Nhãn bucket hiển thị trên trục X (theo kỳ hiện tại) */
  day: string;
  /** Doanh thu kỳ đang chọn */
  current: number;
  /** Doanh thu cùng kỳ năm trước (null nếu bucket đó không có dữ liệu đối ứng) */
  previous: number | null;
  /** Nhãn bucket tương ứng của năm trước, dùng trong tooltip */
  previousLabel?: string;
}

interface RevenueChartProps {
  data: RevenueDataPoint[];
  title?: string;
  subtitle?: string;
  isLoading?: boolean;
  className?: string;
}

interface TooltipPayload {
  value: number | null;
  name: string;
  color: string;
  payload?: RevenueDataPoint;
}

function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: TooltipPayload[];
  label?: string;
}) {
  const { t } = useI18n();
  const money = useMoney();
  // fmtMoney phải nằm trong component: `useMoney()` là hook, không gọi ở cấp module được.
  const fmtMoney = (value: number) =>
    money.format(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  if (!active || !payload?.length) return null;

  const point = payload[0]?.payload;
  const current = payload.find((item) => item.name === 'current')?.value ?? 0;
  const previousEntry = payload.find((item) => item.name === 'previous');
  const previous = previousEntry?.value;
  const delta = previous != null && previous > 0 ? ((current - previous) / previous) * 100 : null;

  return (
    <div className="rounded-xl border border-slate-100 bg-white px-4 py-3 shadow-lg">
      <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="text-xs text-slate-700">
        <span className="font-semibold text-[#2563EB]">{t('thisYear')}:</span> {fmtMoney(current)}
      </p>
      {previous != null && (
        <p className="mt-0.5 text-xs text-slate-700">
          <span className="font-semibold text-[#0D9488]">{t('lastYear')}:</span> {fmtMoney(previous)}
          {point?.previousLabel && <span className="ml-1 text-slate-400">({point.previousLabel})</span>}
        </p>
      )}
      {delta != null && (
        <p className={cn('mt-1 text-xs font-semibold', delta >= 0 ? 'text-emerald-600' : 'text-red-500')}>
          {delta >= 0 ? '▲' : '▼'} {Math.abs(delta).toFixed(1)}%
        </p>
      )}
    </div>
  );
}

export function RevenueChart({
  data,
  title = 'Revenue Over Time',
  subtitle = 'Last 7 days performance',
  isLoading = false,
  className,
}: RevenueChartProps) {
  const { t } = useI18n();
  const money = useMoney();
  const isMobile = useIsMobile();
  // DashboardPage set previous = null cho toàn chuỗi khi kỳ trước không có đơn nào.
  const hasComparison = data.some((item) => item.previous != null);
  const isEmpty = data.length === 0;

  // Chuỗi quá dài thì chấm tròn thành nhiễu; nhưng khi chỉ có 1-2 điểm thì chấm
  // là thứ DUY NHẤT nhìn thấy được (recharts không vẽ được đoạn thẳng từ 1 điểm)
  // nên luôn bật, kể cả trên mobile — bug #29.
  const showDots = data.length <= 2 || (!isMobile && data.length <= 31);

  if (isLoading) {
    return (
      <div className={cn('flex h-[440px] items-center justify-center rounded-xl bg-white p-6 shadow-sm', className)}>
        <div className="h-full w-full animate-pulse rounded-lg bg-slate-100" />
      </div>
    );
  }

  return (
    <div
      className={cn('rounded-xl bg-white p-4 shadow-sm sm:p-6', className)}
      style={{ boxShadow: '0px 1px 2px rgba(0,0,0,0.05)' }}
    >
      <div className="mb-1">
        <h3 className="font-sans text-base font-bold text-slate-900 sm:text-lg">{title}</h3>
        <p className="text-xs text-slate-400 sm:text-sm">{subtitle}</p>
      </div>

      <div className="h-[300px] sm:h-[340px]">
        {isEmpty ? (
          <div className="flex h-full items-center justify-center text-sm text-slate-400">
            No revenue data in this period
          </div>
        ) : (
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 20, right: isMobile ? 4 : 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="gradOnline" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#2563EB" stopOpacity={0.15} />
                <stop offset="95%" stopColor="#2563EB" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gradInStore" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#0D9488" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#0D9488" stopOpacity={0} />
              </linearGradient>
            </defs>
            {/* Không vẽ lưới ngang: nhãn trục Y đã đủ để đọc mức, thêm đường đứt
                quãng chỉ làm nhiễu vùng gradient dưới đường doanh thu. */}
            <XAxis
              dataKey="day"
              axisLine={false}
              tickLine={false}
              interval="preserveStartEnd"
              minTickGap={isMobile ? 44 : 24}
              tickMargin={8}
              tick={{ fill: '#94A3B8', fontSize: isMobile ? 10 : 12, fontWeight: 600 }}
              tickFormatter={(v: string) => v.toUpperCase()}
            />
            <YAxis
              width={isMobile ? 48 : 56}
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#94A3B8', fontSize: isMobile ? 10 : 12 }}
              tickFormatter={(v: number) => money.compact(v, isMobile ? 0 : 1)}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ paddingTop: 12 }}
              iconType="circle"
              iconSize={8}
              formatter={(value: string) => (
                <span style={{ fontSize: 12, color: '#64748B' }}>
                  {value === 'current' ? t('thisYear') : t('lastYear')}
                </span>
              )}
            />
            <Area
              type="monotone"
              dataKey="current"
              name="current"
              stroke="#2563EB"
              strokeWidth={2.5}
              fill="url(#gradOnline)"
              dot={showDots ? { r: 4, fill: '#2563EB', strokeWidth: 0 } : false}
              activeDot={{ r: 6, fill: '#2563EB' }}
            />
            {hasComparison && (
              <Area
                type="monotone"
                dataKey="previous"
                name="previous"
                stroke="#0D9488"
                strokeWidth={2}
                strokeDasharray="4 4"
                fill="url(#gradInStore)"
                // Bug #29: đường "Last Year" trước đây luôn dot={false}. Khi lọc
                // đúng 1 ngày, chuỗi chỉ có 1 điểm nên không vẽ được đoạn thẳng —
                // đường biến mất hoàn toàn trong khi chú giải vẫn hiện chấm tròn
                // "Last Year". Cho phép chấm theo cùng quy tắc với "This Year".
                dot={showDots ? { r: 4, fill: '#0D9488', strokeWidth: 0 } : false}
                activeDot={{ r: 6, fill: '#0D9488' }}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
